<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: $tc('messages.subject', 2),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherGroup in meta.links.otherGroups"
                                :href="otherGroup.url"
                                :key="otherGroup.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherGroup.text }}
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <EmptyState v-if="subjects.length === 0" picture />
        <div v-else class="flex flex-wrap gap-x-2">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
            </AppButton>
        </div>

        <AppWell v-if="tags.blocks.length" class="mt-3">
            <form action="" @submit.prevent="filter">
                <div class="">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-2 relative rounded-md shadow-sm">
                        <input
                            v-model="block_search"
                            type="text"
                            :placeholder="$tc('messages.tags', 2)"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <AppWell v-if="tags.blocks.length" class="mt-3">
                    <div class="md:grid md:grid-cols-12 md:gap-6" v-auto-animate>
                        <AppCard v-for="block in filteredBlocks" :key="block.id" class="sm:col-span-3">
                            <div class="font-medium text-gray-700">
                                {{ block.name }}
                            </div>
                            <div class="mt-3 flex flex-col gap-2" v-auto-animate>
                                <label v-for="tag in block.tags" :key="tag.id">
                                    <BadgePill color="gray">
                                        <input
                                            type="checkbox"
                                            name="tag_ids[]"
                                            :id="tag.id"
                                            v-model="filterForm.tag_ids"
                                            :value="tag.id"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <span class="ml-1">{{ tag.name }}</span>
                                    </BadgePill>
                                </label>
                            </div>
                        </AppCard>
                    </div>

                    <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                        <div class="sm:col-span-8">
                            <div class="flex justify-end mt-5">
                                <SubmitFormButton
                                    :button-text="$t('messages.filter')"
                                    :processing="filterForm.processing"
                                />
                            </div>
                        </div>
                    </div>
                </AppWell>
            </form>
        </AppWell>
        <div v-if="students.length > 0" class="mt-5">
            <form action="" @submit.prevent="submit">
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.students', 1) }}
                                        </th>
                                        <th
                                            v-for="tag in filteredTags"
                                            :key="tag.id"
                                            class="max-w-xs px-1 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                        >
                                            <div class="flex justify-center gap-2">
                                                <Tippy>
                                                    <div class="flex justify-center gap-1 items-center">
                                                        <div :title="tag.name" class="uppercase">
                                                            {{ tag.initials }}
                                                        </div>
                                                        <BadgePill v-if="tag.expected_level">
                                                            {{ tag.expected_level }}
                                                        </BadgePill>
                                                    </div>
                                                    <template #content>
                                                        <div class="font-bold">
                                                            {{ tag.name }}
                                                        </div>
                                                        <div class="mt-3" v-if="tag.expected_level">
                                                            {{
                                                                $t(
                                                                    'messages.expected_level_for_group_explanation_tooltip',
                                                                    {
                                                                        group: group.name,
                                                                        difficulty: tag.expected_level,
                                                                    },
                                                                )
                                                            }}
                                                        </div>
                                                    </template>
                                                </Tippy>
                                                <Tippy interactive>
                                                    <button type="button">
                                                        <IconDotsVertical class="w-4 h-4 text-gray-400" />
                                                    </button>
                                                    <template #content>
                                                        <div>
                                                            {{ $t('messages.set_the_same_value_for_all_students?') }}
                                                        </div>
                                                        <div class="flex flex-col gap-2">
                                                            <div v-for="i in [1, 2, 3, 4, 5]" :key="i">
                                                                <AppButton
                                                                    element="button"
                                                                    type="button"
                                                                    class="font-mono"
                                                                    @click="setTagDifficulty(tag, i)"
                                                                >
                                                                    {{ i }}
                                                                </AppButton>
                                                            </div>
                                                            <div>
                                                                <AppButton
                                                                    element="button"
                                                                    type="button"
                                                                    @click="setTagDifficulty(tag, null)"
                                                                >
                                                                    {{ $t('messages.empty_value') }}
                                                                </AppButton>
                                                            </div>
                                                        </div>
                                                    </template>
                                                </Tippy>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(student, index) in form.students"
                                        :key="student.id"
                                    >
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <div class="flex gap-2 items-center">
                                                <div>{{ student.full_name }}</div>
                                                <div v-if="student.has_adaptation">
                                                    <Tippy>
                                                        <IconLifebuoy class="w-6 h-6 text-indigo-500" />
                                                        <template #content>
                                                            <div>{{ $t('messages.has_adaptation') }}</div>
                                                        </template>
                                                    </Tippy>
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            v-for="tag in student.tags"
                                            :key="tag.tag_id"
                                            class="max-w-sm px-1 py-1 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            <div class="flex justify-center mx-auto">
                                                <span class="tabular-nums">
                                                    <FormQuestionLevelDifficultyInput
                                                        v-model.number="tag.expected_level"
                                                        name="difficulty"
                                                        :max="5"
                                                    />
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end mt-5">
                    <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                </div>
            </form>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import { IconCircleCheckFilled, IconDotsVertical, IconLifebuoy } from '@tabler/icons-vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import BadgePill from '@/components/BadgePill.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import deburr from 'lodash/deburr';
import { Tippy } from 'vue-tippy';
import FormQuestionLevelDifficultyInput from '@/components/Forms/FormQuestionLevelDifficultyInput.vue';

export default {
    components: {
        IconDotsVertical,
        IconLifebuoy,
        FormQuestionLevelDifficultyInput,
        Tippy,
        SubmitFormButton,
        BadgePill,
        AppCard,
        AppWell,
        ActivityMenuOptions,
        AppButton,
        PanelPage,
        EmptyState,
        IconCircleCheckFilled,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'students', 'filters', 'group', 'meta', 'tags'],
    data() {
        return {
            filterForm: this.$inertia.form({
                subject_id: this.filters.subject_id,
                tag_ids: this.filters.tag_ids,
            }),
            form: this.$inertia.form({
                students: this.students,
                tag_ids: this.filters.tag_ids,
                subject_id: this.filters.subject_id,
            }),
            filteredTags: this.tags.selected_tags,
            block_search: '',
        };
    },
    methods: {
        filter() {
            this.filterForm.get(this.meta.links.index, {
                preserveScroll: true,
            });
        },
        submit() {
            this.form.patch(this.meta.links.update, {
                preserveScroll: true,
            });
        },
        setTagDifficulty(tag, i) {
            this.form.students.forEach((student) => {
                student.tags.forEach((studentTag) => {
                    if (studentTag.tag_id === tag.id) {
                        studentTag.expected_level = i;
                    }
                });
            });
        },
    },
    computed: {
        filteredBlocks: function () {
            const search = deburr(this.block_search.toLowerCase());

            if (search === '') {
                return this.tags.blocks;
            }
            return this.tags.blocks
                .map((b) => {
                    return {
                        ...b,
                        tags: b.tags.filter(
                            (t) => deburr(t.name.toLowerCase()).includes(search) || this.form.tag_ids.includes(t.id),
                        ),
                    };
                })
                .filter((b) => b.tags.length > 0);
        },
    },
};
</script>
